import type {DiffEntry} from '@github-ui/diff-lines/types'
import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {useQuery, type QueryKey} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {produce} from 'immer'
import {useCallback} from 'react'
import {getQueryClient} from '@github-ui/react-core/query-client'
import {useComparisonInfo} from '../../stores/selectors'

interface QueryKeyFields {
  basePath: string
  selectedRange: {
    baseOid: string
    headOid: string
  }
  hideWhitespace: boolean
}

function diffEntriesKey({basePath, selectedRange, hideWhitespace}: QueryKeyFields): QueryKey {
  return [
    PageData.diffEntries,
    basePath,
    `baseOid:${selectedRange.baseOid}`,
    `headOid:${selectedRange.headOid}`,
    `hideWhitespace:${hideWhitespace}`,
  ]
}

function diffEntriesOptions(queryKeyFields: QueryKeyFields) {
  return {
    queryKey: diffEntriesKey(queryKeyFields),
    queryFn: async () => {
      return []
    },
  }
}

export function useDiffEntries(basePath: string, initialData?: DiffEntry[]) {
  const comparisonInfo = useComparisonInfo()
  const queryKey = diffEntriesKey({basePath, ...comparisonInfo})

  return useQuery<DiffEntry[]>({
    queryKey,
    queryFn: async () => {
      return initialData ? initialData : []
    },
    initialData,
    staleTime: Infinity,
  })
}

/**
 * Returns a single diff entry for the given path digest
 */
export function useDiffEntry(basePath: string, pathDigest: string) {
  const comparisonInfo = useComparisonInfo()
  const {queryKey, queryFn} = diffEntriesOptions({basePath, ...comparisonInfo})

  return useQuery({
    queryKey,
    queryFn,
    select: useCallback(
      (data: DiffEntry[] | []) => {
        if (!data) return undefined
        return data.find(entry => entry.pathDigest === pathDigest)
      },
      [pathDigest],
    ),
    staleTime: Infinity,
  })
}

export async function loadDiffEntriesForPaths(variables: {
  paths: string[]
  signal?: AbortSignal
  basePath: string
  selectedRange: {
    headOid: string
    baseOid: string
  }
  fullDiff: {
    headOid: string
    baseOid: string
  }
  hideWhitespace: boolean
}) {
  const queryClient = getQueryClient()
  const queryKey = diffEntriesKey(variables)
  const newDiffEntries = await fetchDiffEntries(variables)

  if (newDiffEntries && newDiffEntries.length > 0) {
    queryClient.setQueryData(
      queryKey,
      produce((oldDiffEntriesData: DiffEntry[]) => {
        if (!oldDiffEntriesData) return newDiffEntries

        // Merge old and new without dupes
        for (const newEntry of newDiffEntries) {
          if (oldDiffEntriesData.find(oldEntry => oldEntry.pathDigest === newEntry.pathDigest)) {
            continue
          }
          oldDiffEntriesData.push(newEntry)
        }
      }),
    )
  }
  return newDiffEntries
}

/**
 * Fetches diff entries for given paths
 */
async function fetchDiffEntries({
  basePath,
  fullDiff,
  selectedRange,
  paths,
  signal,
  hideWhitespace,
}: {
  paths: string[]
  signal?: AbortSignal
  basePath: string
  fullDiff: {
    baseOid: string
    headOid: string
  }
  selectedRange: {
    baseOid: string
    headOid: string
  }
  hideWhitespace: boolean
}): Promise<DiffEntry[] | undefined> {
  const params = new URLSearchParams()
  params.append('paths', paths.join(','))
  params.append('w', hideWhitespace ? '1' : '0')

  if (selectedRange.baseOid === fullDiff.baseOid) {
    params.append('range', selectedRange.headOid)
  } else {
    params.append('range', `${selectedRange.baseOid}..${selectedRange.headOid}`)
  }

  const fetchUrl = `${basePath}/page_data/${PageData.diffEntries}?${params.toString()}`

  try {
    const response = await reactFetchJSON(fetchUrl, {signal})
    if (signal?.aborted) return undefined
    const data = await parseJSONWithBetterErrors(response)
    throwErrorsIfBadResponse(response, data)
    return data ?? undefined
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return
    } else {
      throw error
    }
  }
}
